import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { FiX, FiSend, FiMic, FiMicOff, FiVolume2 } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import { colors, spacing, borderRadius, shadows, typography } from '../theme/colors';
import { createTransaction } from '../utils/transactionHelper';
import { subscribeAI } from '../utils/aiBus';

const SUGGESTION_CHIPS = [
  'What were my total sales this month?',
  'Show me top 10 customers',
  'What is my current stock value?',
  'How much do I owe vendors?',
  'Show my profit & loss summary',
  'Create sale invoice for...',
];

const TRAN_TYPE_NAMES = {
  SI: 'Sale Invoice', PI: 'Purchase Invoice', QT: 'Quotation',
  SO: 'Sale Order', PO: 'Purchase Order', CR: 'Cash Receipt',
  BR: 'Bank Receipt', CP: 'Cash Payment', BP: 'Bank Payment',
};

const STORAGE_KEY = 'ai_assistant_chat';

const getStoredMessages = (companyId) => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_${companyId}`);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
};

const storeMessages = (companyId, messages) => {
  try {
    const toStore = messages.slice(-50).map(m => {
      const { action, clarification, ...rest } = m;
      return rest;
    });
    localStorage.setItem(`${STORAGE_KEY}_${companyId}`, JSON.stringify(toStore));
  } catch { /* ignore quota errors */ }
};

const AIAssistant = () => {
  const { isModuleEnabled, activeCompany, currentUser, activeFinancialYear, hasPermission } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const companyId = activeCompany?.id;
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => getStoredMessages(companyId));
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionExecuting, setActionExecuting] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [speakingMsgIdx, setSpeakingMsgIdx] = useState(null);

  // Module on AND (owner / admin, or the role's "Ask AI" tick) — it reads all company data.
  const enabled = isModuleEnabled('ai_assistant') && !!hasPermission && hasPermission('ai', 'askAI', 'toggle');

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.abort();
      window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    if (companyId && messages.length > 0) {
      storeMessages(companyId, messages);
    }
  }, [messages, companyId]);

  useEffect(() => {
    if (companyId) {
      const stored = getStoredMessages(companyId);
      setMessages(stored.length > 0 ? stored : []);
    }
  }, [companyId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setMessages(prev => {
      if (prev.length === 0) {
        const welcome = [{
          role: 'assistant',
          content: `Hi! I'm your AI assistant for **${activeCompany?.companyName || 'your company'}**. Ask me anything — balances, invoices, stock, reports — and I'll give you a quick answer with links to navigate directly.`
        }];
        if (companyId) storeMessages(companyId, welcome);
        return welcome;
      }
      return prev;
    });
  }, [activeCompany?.companyName, companyId]);

  // Let the header "Ask AI" button (or anywhere) open this panel via aiBus.
  useEffect(() => subscribeAI(() => handleOpen()), [handleOpen]);

  const handleActionResponse = useCallback((data) => {
    const assistantMsg = {
      role: 'assistant',
      content: data.answer,
      sources: data.sources,
    };

    if (data.action && data.action.type === 'CREATE_TRANSACTION') {
      assistantMsg.action = data.action;
      setPendingAction(data.action);
    }

    if (data.clarification && data.clarification.type === 'CLARIFICATION_NEEDED') {
      assistantMsg.clarification = data.clarification;
    }

    setMessages(prev => [...prev, assistantMsg]);
  }, []);

  const handleSend = useCallback(async (text) => {
    const question = (text || input).trim();
    if (!question || isLoading) return;

    const userMsg = { role: 'user', content: question };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const askAI = httpsCallable(functions, 'askAIAssistant');
      const history = [...messages, userMsg].slice(-10).map(m => ({
        role: m.role, content: m.content
      }));
      const result = await askAI({ question, history, companyId });
      handleActionResponse(result.data);
    } catch (error) {
      console.error('AI Assistant error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages, handleActionResponse]);

  const handleConfirmAction = useCallback(async (action) => {
    if (!action || actionExecuting) return;
    setActionExecuting(true);

    try {
      const txnData = {
        ...action.transactionData,
        financialYearId: activeFinancialYear?.id,
        status: 'Draft',
      };

      const result = await createTransaction(companyId, txnData, currentUser.uid);
      const typeName = TRAN_TYPE_NAMES[txnData.tranTypeCode] || txnData.tranTypeCode;

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `${typeName} created successfully!\n\nDocument: **${result.docNumber}**\nStatus: Draft\n\nYou can review and post it from the ${typeName} page.`
      }]);
      setPendingAction(null);
    } catch (error) {
      console.error('Transaction creation error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Failed to create transaction: ${error.message || 'Unknown error'}. Please try again.`
      }]);
    } finally {
      setActionExecuting(false);
    }
  }, [actionExecuting, activeFinancialYear, companyId, currentUser]);

  const handleCancelAction = useCallback(() => {
    setPendingAction(null);
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Transaction cancelled.'
    }]);
  }, []);

  const handleClarificationClick = useCallback((option, clarification) => {
    const field = clarification.field || 'entity';
    handleSend(`Use ${field}: "${option.name}" (${option.code})`);
  }, [handleSend]);

  // ── Parse message content for links and bold text ──
  const renderMessageContent = useCallback((content) => {
    if (!content) return null;
    const regex = /(\*\*(.+?)\*\*|\[([^\]]+)\]\(\/([^)]+)\))/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let keyIdx = 0;

    while ((match = regex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      if (match[2]) {
        parts.push(<strong key={`b${keyIdx++}`}>{match[2]}</strong>);
      } else if (match[3] && match[4]) {
        const linkText = match[3];
        const fullPath = '/' + match[4];
        const qIdx = fullPath.indexOf('?');
        const path = qIdx >= 0 ? fullPath.substring(0, qIdx) : fullPath;
        const state = {};
        if (qIdx >= 0) {
          const params = new URLSearchParams(fullPath.substring(qIdx + 1));
          params.forEach((value, key) => { state[key] = value; });
        }
        parts.push(
          <AppLink
            key={`l${keyIdx++}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
              navigate(path, Object.keys(state).length > 0 ? { state } : undefined);
            }}
          >
            {linkText}
          </AppLink>
        );
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }
    return parts.length > 0 ? parts : content;
  }, [navigate]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // ── Voice Input (Speech-to-Text) ──
  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Voice input is not supported in your browser. Please try Chrome or Edge.'
      }]);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setInput(transcript);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // ── Voice Reply (Text-to-Speech) ──
  const speakMessage = useCallback((text, msgIdx) => {
    window.speechSynthesis.cancel();

    if (speakingMsgIdx === msgIdx) {
      setSpeakingMsgIdx(null);
      return;
    }

    const cleanText = text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/#{1,6}\s/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/\n+/g, '. ');

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.onend = () => setSpeakingMsgIdx(null);
    utterance.onerror = () => setSpeakingMsgIdx(null);

    setSpeakingMsgIdx(msgIdx);
    window.speechSynthesis.speak(utterance);
  }, [speakingMsgIdx]);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis.cancel();
    setSpeakingMsgIdx(null);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!enabled) return null;

  const showChips = messages.length === 1 && messages[0].role === 'assistant';

  const renderActionCard = (action) => {
    const summary = action.displaySummary;
    if (!summary) return null;

    return (
      <ActionCard>
        <ActionCardHeader>
          <ActionCardTitle>{summary.type}</ActionCardTitle>
          <ActionCardParty>{summary.party}</ActionCardParty>
        </ActionCardHeader>
        {summary.items && summary.items.length > 0 && (
          <ActionCardItems>
            {summary.items.map((item, i) => (
              <ActionCardItem key={i}>
                <span>{item.name}</span>
                <span>{item.qty} x {item.rate?.toLocaleString()} = {item.amount?.toLocaleString()}</span>
              </ActionCardItem>
            ))}
          </ActionCardItems>
        )}
        <ActionCardTotal>
          Total: {summary.total?.toLocaleString()}
        </ActionCardTotal>
        {pendingAction === action && (
          <ActionCardButtons>
            <ActionButton
              $variant="confirm"
              onClick={() => handleConfirmAction(action)}
              disabled={actionExecuting}
            >
              {actionExecuting ? 'Creating...' : 'Create Draft'}
            </ActionButton>
            <ActionButton
              $variant="cancel"
              onClick={handleCancelAction}
              disabled={actionExecuting}
            >
              Cancel
            </ActionButton>
          </ActionCardButtons>
        )}
      </ActionCard>
    );
  };

  const renderClarification = (clarification) => {
    if (!clarification || !clarification.options) return null;
    return (
      <ClarificationContainer>
        {clarification.options.map((opt, i) => (
          <ClarificationOption key={i} onClick={() => handleClarificationClick(opt, clarification)}>
            {opt.name} ({opt.code})
          </ClarificationOption>
        ))}
      </ClarificationContainer>
    );
  };

  return (
    <>
      {/* On desktop the trigger now lives in the header (before Logout). Keep the
          floating button on mobile, where the header actions are hidden. */}
      {!isOpen && isMobile && (
        <FloatingButton $isMobile={isMobile} onClick={handleOpen}>
          <PulseRing />
          <FabIcon>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74L12 2z" />
            </svg>
          </FabIcon>
          <FabLabel>Ask AI</FabLabel>
        </FloatingButton>
      )}

      {isOpen && (
        <ChatPanel $isMobile={isMobile}>
          <ChatHeader>
            <ChatHeaderInfo>
              <ChatTitle>AI Assistant</ChatTitle>
              <ChatSubtitle>{activeCompany?.companyName || 'Business Assistant'}</ChatSubtitle>
            </ChatHeaderInfo>
            <CloseButton onClick={() => { setIsOpen(false); stopListening(); stopSpeaking(); }}>
              <FiX size={20} />
            </CloseButton>
          </ChatHeader>

          <MessagesArea>
            {messages.map((msg, idx) => (
              <MessageRow key={idx} $isUser={msg.role === 'user'}>
                <MessageBubble $isUser={msg.role === 'user'}>
                  {msg.role === 'assistant' ? renderMessageContent(msg.content) : msg.content}
                  {msg.action && renderActionCard(msg.action)}
                  {msg.clarification && renderClarification(msg.clarification)}
                  {msg.role === 'assistant' && msg.content && (
                    <SpeakButton
                      onClick={() => speakMessage(msg.content, idx)}
                      title={speakingMsgIdx === idx ? 'Stop speaking' : 'Read aloud'}
                      $isSpeaking={speakingMsgIdx === idx}
                    >
                      <FiVolume2 size={13} />
                    </SpeakButton>
                  )}
                </MessageBubble>
              </MessageRow>
            ))}

            {showChips && (
              <ChipsContainer>
                {SUGGESTION_CHIPS.map((chip, idx) => (
                  <SuggestionChip key={idx} onClick={() => handleSend(chip)}>
                    {chip}
                  </SuggestionChip>
                ))}
              </ChipsContainer>
            )}

            {isLoading && (
              <MessageRow $isUser={false}>
                <MessageBubble $isUser={false}>
                  <LoadingDots>
                    <Dot $delay="0s" />
                    <Dot $delay="0.2s" />
                    <Dot $delay="0.4s" />
                  </LoadingDots>
                </MessageBubble>
              </MessageRow>
            )}
            <div ref={messagesEndRef} />
          </MessagesArea>

          <InputBar>
            <ChatInput
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask or give a command..."
              disabled={isLoading}
            />
            <MicButton
              onClick={toggleListening}
              disabled={isLoading}
              $isListening={isListening}
              title={isListening ? 'Stop listening' : 'Voice input'}
            >
              {isListening && <ListeningPulse />}
              {isListening ? <FiMicOff size={18} /> : <FiMic size={18} />}
            </MicButton>
            <SendButton onClick={() => handleSend()} disabled={!input.trim() || isLoading}>
              <FiSend size={18} />
            </SendButton>
          </InputBar>
        </ChatPanel>
      )}
    </>
  );
};

// ── Animations ──
const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  70% { transform: scale(1.35); opacity: 0; }
  100% { transform: scale(1.35); opacity: 0; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const fabEntry = keyframes`
  0% { transform: scale(0) rotate(-90deg); opacity: 0; }
  60% { transform: scale(1.1) rotate(5deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const sparkle = keyframes`
  0%, 100% { transform: rotate(0deg) scale(1); }
  25% { transform: rotate(8deg) scale(1.1); }
  75% { transform: rotate(-4deg) scale(0.95); }
`;

const listeningPulse = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.8); opacity: 0; }
  100% { transform: scale(1.8); opacity: 0; }
`;

const speakingPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

// ── Styled Components ──
const FloatingButton = styled.button`
  position: fixed;
  right: 20px;
  bottom: ${props => props.$isMobile ? '76px' : '20px'};
  height: 48px;
  padding: 0 18px 0 14px;
  border-radius: 24px;
  background: linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 50%, ${colors.primary.main} 100%);
  background-size: 200% auto;
  color: ${colors.text.white};
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow:
    0 4px 14px rgba(128, 0, 32, 0.4),
    0 1px 4px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
  z-index: 1000;
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease;
  animation: ${fabEntry} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;

  &:hover {
    transform: translateY(-2px) scale(1.04);
    box-shadow:
      0 8px 24px rgba(128, 0, 32, 0.45),
      0 2px 8px rgba(0, 0, 0, 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    animation: ${shimmer} 1.5s ease infinite;
    background-size: 200% auto;
  }

  &:active {
    transform: translateY(0) scale(0.97);
    box-shadow: 0 2px 8px rgba(128, 0, 32, 0.35);
  }
`;

const PulseRing = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 24px;
  border: 2px solid ${colors.primary.main};
  animation: ${pulseRing} 2.5s ease-out infinite;
  pointer-events: none;
`;

const FabIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${sparkle} 3s ease-in-out infinite;
`;

const FabLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const ChatPanel = styled.div`
  position: fixed;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  background: ${colors.background.default};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  animation: ${fadeInUp} 0.25s ease;

  ${props => props.$isMobile ? css`
    inset: 0;
    border-radius: 0;
  ` : css`
    right: 24px;
    bottom: 24px;
    width: 400px;
    height: 560px;
    border-radius: ${borderRadius.lg};
    overflow: hidden;
  `}
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${colors.primary.main};
  color: ${colors.text.white};
  flex-shrink: 0;
`;

const ChatHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ChatTitle = styled.span`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.semibold};
`;

const ChatSubtitle = styled.span`
  font-size: ${typography.fontSize.xs};
  opacity: 0.8;
`;

const CloseButton = styled.button`
  background: transparent;
  color: ${colors.text.white};
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral[300]};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div`
  max-width: 90%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: ${typography.fontSize.sm};
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;

  ${props => props.$isUser ? css`
    background: ${colors.primary.main};
    color: ${colors.text.white};
    border-bottom-right-radius: 4px;
  ` : css`
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    border-bottom-left-radius: 4px;
  `}
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
`;

const SuggestionChip = styled.button`
  padding: 6px 12px;
  border-radius: 16px;
  background: transparent;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main};
  font-size: ${typography.fontSize.xs};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: ${colors.primary.main};
    color: ${colors.text.white};
  }
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 0;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.text.secondary};
  animation: ${bounce} 1.2s infinite ease-in-out;
  animation-delay: ${props => props.$delay};
`;

const InputBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 12px;
  border-top: 1px solid ${colors.border.light};
  flex-shrink: 0;
  background: ${colors.background.default};
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 1px solid ${colors.border.light};
  border-radius: 20px;
  font-size: ${typography.fontSize.sm};
  outline: none;
  transition: border-color 0.15s ease;
  background: ${colors.neutral[50]};
  min-width: 0;

  &:focus {
    border-color: ${colors.primary.main};
  }

  &:disabled {
    opacity: 0.6;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const SendButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${colors.primary.main};
  color: ${colors.text.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const MicButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.$isListening ? colors.error.main : 'transparent'};
  color: ${props => props.$isListening ? colors.text.white : colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;

  &:hover:not(:disabled) {
    color: ${props => props.$isListening ? colors.text.white : colors.primary.main};
    background: ${props => props.$isListening ? colors.error.dark : colors.neutral[100]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ListeningPulse = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: ${colors.error.main};
  animation: ${listeningPulse} 1.5s ease-out infinite;
  pointer-events: none;
`;

const SpeakButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: transparent;
  color: ${props => props.$isSpeaking ? colors.primary.main : colors.text.tertiary};
  cursor: pointer;
  transition: all 0.15s ease;
  margin-top: 6px;
  padding: 0;
  flex-shrink: 0;

  ${props => props.$isSpeaking && css`
    animation: ${speakingPulse} 1s ease-in-out infinite;
  `}

  &:hover {
    color: ${colors.primary.main};
    background: rgba(0, 0, 0, 0.05);
  }
`;

// ── Action Card Styles ──
const ActionCard = styled.div`
  margin-top: 10px;
  background: ${colors.background.default};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  overflow: hidden;
`;

const ActionCardHeader = styled.div`
  padding: 10px 12px;
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.border.light};
`;

const ActionCardTitle = styled.div`
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.sm};
  color: ${colors.primary.main};
`;

const ActionCardParty = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  margin-top: 2px;
`;

const ActionCardItems = styled.div`
  padding: 8px 12px;
`;

const ActionCardItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${typography.fontSize.xs};
  padding: 4px 0;
  color: ${colors.text.primary};

  & > span:last-child {
    color: ${colors.text.secondary};
    white-space: nowrap;
    margin-left: 8px;
  }
`;

const ActionCardTotal = styled.div`
  padding: 8px 12px;
  border-top: 1px solid ${colors.border.light};
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.sm};
  text-align: right;
  color: ${colors.text.primary};
`;

const ActionCardButtons = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid ${colors.border.light};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.15s ease;

  ${props => props.$variant === 'confirm' ? css`
    background: ${colors.success.main};
    color: ${colors.text.white};
    &:hover:not(:disabled) { background: ${colors.success.dark}; }
  ` : css`
    background: transparent;
    color: ${colors.text.secondary};
    border: 1px solid ${colors.border.light};
    &:hover:not(:disabled) { background: ${colors.neutral[100]}; }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// ── Clarification Styles ──
const ClarificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const ClarificationOption = styled.button`
  padding: 8px 12px;
  border-radius: ${borderRadius.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.light};
  font-size: ${typography.fontSize.xs};
  color: ${colors.primary.main};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.primary.main};
    color: ${colors.text.white};
    border-color: ${colors.primary.main};
  }
`;

const AppLink = styled.span`
  color: ${colors.primary.main};
  text-decoration: underline;
  cursor: pointer;
  font-weight: ${typography.fontWeight.medium};

  &:hover {
    color: ${colors.primary.dark};
  }
`;

export default AIAssistant;
